import {
  Box,
  Divider,
  Chip as MuiChip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import {
  Alert,
  BackLink,
  LinkedFileBox,
  MarkdownDisplay,
  PageHeader,
  PaperContainer,
} from "@pharmupp/p3-front-commons"
import type { ReactNode } from "react"
import { Calendar, Eye, Package, Send } from "react-feather"
import { useParams } from "react-router-dom"
import { ProofDocumentCount } from "../../commons"
import { HiddenBtn } from "../common/components/HiddenBtn"
import { OperationStateEnum } from "../model"
import { Sidebar } from "./Sidebar"
import {
  DocumentsGrid,
  HeadSectionTitle,
  PointsChip,
  PriceChip,
  RegisterStatusChip,
  SubmitDocumentsButton,
  SubscribeButton,
  UnsubscribeButton,
} from "./components"
import {
  useAdhMarketingOPDisplayApi,
  useHideParticipationApi,
} from "./useAdhMarketingOPDisplayApi"

const invalidStates = [OperationStateEnum.OUTDATED, OperationStateEnum.EXPIRED]
const registerStates = [
  OperationStateEnum.WAITING,
  OperationStateEnum.STARTED,
  OperationStateEnum.REVIEWED,
  OperationStateEnum.REJECTED,
  OperationStateEnum.VALIDATED,
]

export const AdhMarketingOPDisplay = () => {
  const theme = useTheme()
  const { id: operationId } = useParams()
  const { operation, loading } = useAdhMarketingOPDisplayApi({ operationId })

  return (
    <>
      {/* BACK BTN */}
      <BackLink to="./..">Opérations marketing</BackLink>

      {/* SIDEBAR */}
      {!!operation && <Sidebar operation={operation} />}

      {/* HEADER */}
      <PageHeader title={operation?.name || ""} level={2}>
        {!!operation && (
          <Stack direction="row" spacing={1}>
            {!!operation.nbLoyaltyPoints &&
              operation.state !== OperationStateEnum.VALIDATED && (
                <PointsChip
                  nbLoyaltyPoints={operation.nbLoyaltyPoints}
                  isMissed={
                    operation.hidden || invalidStates.includes(operation.state)
                  }
                />
              )}
            {!!operation.participationBonus && (
              <PriceChip>{operation?.participationBonus}</PriceChip>
            )}
            {registerStates.includes(operation.state) && (
              <RegisterStatusChip
                nbLoyaltyPoints={operation.nbLoyaltyPoints || 0}
                isValidated={operation.state === OperationStateEnum.VALIDATED}
              />
            )}
          </Stack>
        )}
      </PageHeader>

      {/* CONTENT */}
      <Box gridArea="main" mb={4}>
        <PaperContainer loading={loading}>
          {!!operation && (
            <Stack spacing={6}>
              <Box display="grid" gridTemplateColumns="2fr 1fr" gap={17.5}>
                {/* DELIVERY INFO */}
                <Stack spacing={2} borderRadius="5px" color="common.darkBlue">
                  <Stack direction="row" spacing={1}>
                    <Package color={theme.palette.tertiary.main} />
                    <HeadSectionTitle>Livraison des Kits</HeadSectionTitle>
                  </Stack>

                  <Divider />

                  <Box>
                    <Stack direction="row" mb={1}>
                      <Typography>Période de commande :&nbsp;</Typography>
                      <Typography fontWeight={800}>
                        du {operation.kitOrderStartDateFormatted} au{" "}
                        {operation.kitOrderEndDateFormatted}
                      </Typography>
                    </Stack>
                    <Stack direction="row">
                      <Typography>Livraison :&nbsp;</Typography>
                      <Typography fontWeight={800}>
                        à partir du {operation.deliveryDate}
                      </Typography>
                      {!!operation.deliveryCarrier && (
                        <Typography fontWeight={800}>
                          &nbsp;-&nbsp;Par {operation.deliveryCarrier}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Stack>

                {/* VALIDITY */}
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1}>
                    <Calendar color={theme.palette.common.electricBlue} />
                    <HeadSectionTitle>Validité</HeadSectionTitle>
                  </Stack>

                  <Divider />

                  <Stack>
                    <Typography fontWeight={500} color="common.electricBlue">
                      Du {operation.validityStartDate}
                    </Typography>
                    <Typography fontWeight={500} color="common.electricBlue">
                      Au {operation.validityEndDate}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {/* DESCRIPTION */}
              {!!operation.description && (
                <MarkdownDisplay>{operation.description}</MarkdownDisplay>
              )}

              {/* PARTNER LABORATORIES */}
              {operation.partnerLaboratories.length > 0 && (
                <Stack gridArea="partnerLabs" spacing={2}>
                  <HeadSectionTitle>Laboratoires partenaires</HeadSectionTitle>
                  <Divider />
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {operation.partnerLaboratories.map(({ id, name }) => (
                      <MuiChip
                        key={id}
                        label={name}
                        variant="outlined"
                        sx={{
                          color: "common.darkBlue",
                          textTransform: "uppercase",
                        }}
                      />
                    ))}
                  </Box>
                </Stack>
              )}

              {/* AMM WARNING */}
              {!!operation.hasAmmProducts && (
                <Alert severity="info" variant="standard">
                  <span>
                    <b>
                      {`${operation.nbAmmProducts} ${
                        operation.nbAmmProducts > 1 ? "produits" : "produit"
                      } AMM :`}
                    </b>
                    &nbsp;mise en avant possible en Libre-Accès ou inter-comptoir
                    dédié
                  </span>
                </Alert>
              )}

              <Stack spacing={6} divider={<Divider />}>
                {/* LINKED DOCUMENTS: TODO: when wedia is ready */}
                {!!false && (
                  <Stack spacing={2}>
                    <Typography fontWeight={600} color="common.darkBlue" mb={1}>
                      Documents liés
                    </Typography>
                    <LinkedFileBox documentLink="www.test.com" />
                    <LinkedFileBox documentLink="www.test.com" />
                  </Stack>
                )}

                {/* COMPLETION PROOFS */}
                <Stack spacing={4}>
                  <Stack color="common.darkBlue">
                    <Stack direction="row" spacing={1}>
                      <Eye />
                      <Typography fontWeight={600}>Justificatif</Typography>
                    </Stack>
                    <Stack direction="row" mt={2}>
                      <ProofDocumentCount count={operation.nbProofDocuments} />
                    </Stack>
                    <Typography mt={2}>
                      {operation.completionProofDetails}
                    </Typography>
                  </Stack>

                  {/* PROOF DOCUMENTS */}
                  {!!operation?.proofDocuments?.length && (
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} color="common.darkBlue">
                        <Send />
                        <Typography fontWeight={600}>
                          Justificatifs envoyés
                        </Typography>
                      </Stack>
                      <DocumentsGrid
                        proofDocuments={operation.proofDocuments}
                        aspectRatio="16/9"
                        showStatus
                        showRejectionReason
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack>

              {/* ACTIONS */}
              {!!operation && (
                <BottomActions
                  state={operation.state}
                  operationId={operationId!}
                  hidden={operation.hidden}
                />
              )}
            </Stack>
          )}
        </PaperContainer>
      </Box>
    </>
  )
}

const BottomActions = ({
  state,
  operationId,
  hidden = false,
}: {
  state: OperationStateEnum
  operationId: string
  hidden?: boolean
}) => {
  const { hideParticipation } = useHideParticipationApi({ operationId })

  switch (state) {
    case OperationStateEnum.REGISTRABLE:
    case OperationStateEnum.UPCOMING:
      return (
        <ActionsRow>
          <SubscribeButton
            operationId={operationId}
            hidden={hidden}
            disabled={state === OperationStateEnum.UPCOMING}
          />
          <HiddenBtn hidden={hidden} handleClick={hideParticipation} />
        </ActionsRow>
      )
    case OperationStateEnum.STARTED:
      return (
        <ActionsRow>
          <SubmitDocumentsButton operationId={operationId}>
            Soumettre justificatif
          </SubmitDocumentsButton>
          <UnsubscribeButton operationId={operationId} />
        </ActionsRow>
      )
    case OperationStateEnum.REJECTED:
      return (
        <ActionsRow>
          <SubmitDocumentsButton operationId={operationId}>
            Nouveau justificatif
          </SubmitDocumentsButton>
        </ActionsRow>
      )
    case OperationStateEnum.WAITING:
      return (
        <ActionsRow>
          <UnsubscribeButton operationId={operationId} />
        </ActionsRow>
      )
    default:
      return null
  }
}

const ActionsRow = ({ children }: { children: ReactNode }) => (
  <>
    <Divider />
    <Stack direction="row" justifyContent="center" spacing={2} mt={5}>
      {children}
    </Stack>
  </>
)
